import React, { useState, useRef, useEffect } from 'react';
import { Book, ArrowRight, ArrowLeft, Loader2, Heart, Moon, Library, Play, Pause, Volume2, Music, Sparkles, Users } from 'lucide-react';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { StoryLoadingAnimation } from './StoryLoadingAnimation';
import { AudioLoadingAnimation } from './AudioLoadingAnimation';
import { NonSubscriberLoadingAnimation } from './NonSubscriberLoadingAnimation';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import { detectStoryMoments, shouldTriggerSound, type StoryMoment } from '@/utils/storyMomentDetector';
import { CollaborativeStoryMode } from './CollaborativeStoryMode';
import { MerchandiseCatalog } from './MerchandiseCatalog';
import { StickerGenerator } from './StickerGenerator';
import { StoryEnhancements } from './StoryEnhancements';
import { ShareStory } from './ShareStory';
import { CharacterImageUpload } from './CharacterImageUpload';
import { CharacterGalleryCarousel } from './CharacterGalleryCarousel';
import { CartoonCharacterSelector } from './CartoonCharacterSelector';
import { IllustrationsTogglePanel } from './IllustrationsTogglePanel';
import ErrorBoundary from './ErrorBoundary';
import Footer from './ui/footer';
import { useAuth } from '@/hooks/useAuth';
import { useStoryRedirect, type StoryRedirectOptions } from '@/hooks/useStoryRedirect';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useGender } from '@/contexts/GenderContext';
import { PenelopeEasterEggs, PenelopeWelcome } from './PenelopeEasterEggs';
import { calculateAge } from '@shared/utils';
import type { ChildProfile } from '@shared/schema';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { useToast } from '@/hooks/use-toast';

const TRANSLATIONS = {
  "en-US": {
    "ageTitle": "How old is your little one?",
    "ageSubtitle": "This helps us choose the perfect vocabulary and themes",
    "agePlaceholder": "Enter age (2-12)",
    "continueButton": "Continue",
    "step1of7": "Step 1 of 7",
    "genderTitle": "Tell us about your child",
    "genderSubtitle": "We'll use the right pronouns and perspective",
    "boy": "boy",
    "girl": "girl",
    "other": "other",
    "backButton": "Back",
    "step2of7": "Step 2 of 7",
    "languageTitle": "Choose Story Language",
    "languageSubtitle": "Select the language for your personalized story",
    "step3of7": "Step 3 of 7",
    "childNameTitle": "This story is for...",
    "childNameSubtitle": "Let's personalize the story",
    "childNamePlaceholder": "Enter child's name",
    "step4of7": "Step 4 of 7",
    "interestsTitle": "What does your child love?",
    "interestsSubtitle": "Pick 1-3 things that spark their imagination",
    "animals": "Animals",
    "spaceStars": "Space & Stars",
    "oceanSeaLife": "Ocean & Sea Life",
    "dinosaurs": "Dinosaurs",
    "magicFantasy": "Magic & Fantasy",
    "sports": "Sports",
    "music": "Music",
    "artDrawing": "Art & Drawing",
    "natureForest": "Nature & Forest",
    "superheroes": "Superheroes",
    "vehiclesTransportation": "Vehicles & Transportation",
    "cookingFood": "Cooking & Food",
    "scienceExperiments": "Science & Experiments",
    "step5of7": "Step 5 of 7",
    "styleTitle": "What kind of story would you like?",
    "styleSubtitle": "Choose the tone that fits bedtime best",
    "funnyLabel": "Funny & Silly",
    "funnyDesc": "Giggles and laughs throughout",
    "adventurousLabel": "Adventurous & Exciting",
    "adventurousDesc": "Thrilling quests and discoveries",
    "gentleLabel": "Gentle & Calming",
    "gentleDesc": "Peaceful and soothing",
    "magicalLabel": "Magical & Enchanting",
    "magicalDesc": "Fantasy and wonder",
    "educationalLabel": "Educational & Learning",
    "educationalDesc": "Fun facts and knowledge",
    "step6of7": "Step 6 of 7",
    "lengthTitle": "How long should the story be?",
    "lengthSubtitle": "Choose the perfect length for bedtime",
    "shortLabel": "Short Story",
    "shortDesc": "Perfect for busy nights (3-5 minutes)",
    "longLabel": "Long Story",
    "longDesc": "Perfect for cozy story time (8-12 minutes)",
    "step7of7": "Step 7 of 7",
    "lessonTitle": "What lesson should we include?",
    "lessonSubtitle": "What important message should they learn?",
    "lessonPlaceholder": "Examples: Being kind to others, trying new things, sharing is caring, being brave when scared...",
    "createStoryButton": "Create Story",
    "generatingTitle": "Creating your magical story...",
    "generatingSubtitle": "Our storytelling magic is working...",
    "storyReadyTitle": "Your bedtime story is ready!",
    "storyReadySubtitle": "Sweet dreams are just a story away!",
    "sweetDreams": "Sweet dreams!",
    "createAnotherStory": "Create Another Story",
    "errorMessage": "Sorry, there was an error generating your story. Please try again."
  }
};

const locale = 'en-US' as const;
const t = (key: string) => TRANSLATIONS[locale]?.[key as keyof typeof TRANSLATIONS['en-US']] || key;

// Navigation is now handled by the Navigation component

// Footer component imported above

const BedtimeStoryGeneratorInner = () => {
  const currentLang = 'en-US' as const; // Default language for now
  const t = (key: string) => TRANSLATIONS[currentLang][key as keyof typeof TRANSLATIONS['en-US']] || key;
  
  // Get user authentication state
  const { user, hasActiveSubscription, isFounder, isLoading: isAuthLoading } = useAuth();
  const { redirectToStory } = useStoryRedirect();

  // Initialize sound effects
  const soundEffects = useSoundEffects();

  // Get gender context
  const { selectedGender, setSelectedGender, setSelectedChild, resetGender, resetGenderAfterStory } = useGender();
  
  // Toast notifications
  const { toast } = useToast();

  // Function to get gender-based logo
  // Step constants
  const WizardStep = {
    ChildSelect: -1,
    Interests: 0,
    Style: 1,
    Length: 2,
    Lesson: 3,
    Generating: 4,
    Display: 5
  } as const;

  type WizardStepType = typeof WizardStep[keyof typeof WizardStep];

  const TOTAL_STEPS = 4; // Only count visible user-facing steps (Interests through Lesson)

  // Step Indicator Component
  const StepIndicator = ({ step, total = TOTAL_STEPS }: { step: number; total?: number }) => {
    if (step < 1 || step > total) return null;
    
    const percentage = Math.round((step / total) * 100);
    
    return (
      <div className="mt-8 text-center">
        <div className="text-sm text-gray-600">Step {step} of {total}</div>
        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
          <div 
            className="bg-gradient-to-r from-purple-600 to-teal-600 h-2 rounded-full transition-all duration-300" 
            style={{width: `${percentage}%`}}
          ></div>
        </div>
      </div>
    );
  };

  const getGenderBasedLogo = () => {
    switch (selectedGender) {
      case 'girl':
        return '/logo-pink.png';
      case 'boy':
        return '/logo-blue.png';
      case 'other':
        return '/logo-purple.png';
      default:
        return '/logo.png';
    }
  };

  const [currentStep, setCurrentStep] = useState<WizardStepType>(WizardStep.ChildSelect);
  const [isGenerating, setIsGenerating] = useState(false);
  const [storyText, setStoryText] = useState('');
  const [storyTitle, setStoryTitle] = useState('');
  const [storyId, setStoryId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    language: 'en', // Default to English
    childName: '',
    childProfileId: null as number | null, // ID of selected child profile
    characterProfileId: null as number | null,
    interests: [] as string[],
    style: '',
    length: '',
    lesson: '',
    enableIllustrations: false,
    characterImageUrl: '',
    characterImageData: null as any,
    selectedCartoonCharacter: null as any,
    selectedCharacterFromGallery: null as any, // New field for gallery character selection
    selectedCharacterStudioCharacter: null as any // New field for character studio character selection
  });

  // Fetch child profiles
  const { data: childProfiles = [] } = useQuery<ChildProfile[]>({
    queryKey: ['/api/child-profiles'],
    enabled: !!user,
  });

  // Form data will be populated when a child profile is selected via handleChildSelection

  // Character selection state
  const [showCharacterUpload, setShowCharacterUpload] = useState(false);
  // Audio player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioText, setAudioText] = useState<string>(''); // Store the actual text used for audio
  const [highlightingEnabled, setHighlightingEnabled] = useState(true); // Toggle for word highlighting
  const [soundEffectsEnabled, setSoundEffectsEnabled] = useState(true); // Toggle for sound effects
  const [storyMoments, setStoryMoments] = useState<StoryMoment[]>([]); // Detected story moments
  const [lastSoundIndex, setLastSoundIndex] = useState(-10); // Track last sound effect to avoid overlaps
  const [loadingStage, setLoadingStage] = useState<'thinking' | 'writing' | 'polishing'>('thinking');
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [pendingGeneration, setPendingGeneration] = useState(false);
  
  // Illustration progress state for SSE
  interface IllustrationProgress {
    current: number;
    total: number;
    description?: string;
    percentage: number;
    stage: 'story' | 'planning' | 'illustrations' | 'finishing' | 'saving' | 'complete';
    message?: string;
  }
  
  const [illustrationProgress, setIllustrationProgress] = useState<IllustrationProgress | null>(null);
  const [useSSE, setUseSSE] = useState(true); // Flag to enable/disable SSE

  // Collaborative story mode state
  const [isCollaborative, setIsCollaborative] = useState(false);
  const [plotTwists, setPlotTwists] = useState<{ id: string; text: string }[]>([]);

  // AI suggestions state
  const [lessonSuggestions, setLessonSuggestions] = useState<string[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Story nickname dialog state
  const [showNicknameDialog, setShowNicknameDialog] = useState(false);

  const interests = [
    t('animals'), t('spaceStars'), t('oceanSeaLife'), t('dinosaurs'), t('magicFantasy'),
    t('sports'), t('music'), t('artDrawing'), t('natureForest'), t('superheroes'),
    t('vehiclesTransportation'), t('cookingFood'), t('scienceExperiments')
  ];

  const styles = [
    { value: 'funny', label: t('funnyLabel'), emoji: '😄', desc: t('funnyDesc') },
    { value: 'adventurous', label: t('adventurousLabel'), emoji: '🌟', desc: t('adventurousDesc') },
    { value: 'gentle', label: t('gentleLabel'), emoji: '🌙', desc: t('gentleDesc') },
    { value: 'magical', label: t('magicalLabel'), emoji: '✨', desc: t('magicalDesc') },
    { value: 'educational', label: t('educationalLabel'), emoji: '📚', desc: t('educationalDesc') }
  ];

  const handleChildSelection = (childProfile: any) => {
    const age = childProfile.birthday ? calculateAge(childProfile.birthday) : '';
    setFormData(prev => ({
      ...prev,
      childProfileId: childProfile.id,
      age: age ? String(age) : '',
      gender: childProfile.gender || '',
      language: childProfile.preferredLanguage || 'en',
      childName: childProfile.name || ''
    }));
    
    // Update gender and child context
    if (childProfile.gender) {
      setSelectedGender(childProfile.gender as 'boy' | 'girl' | 'other');
    }
    setSelectedChild(childProfile);
    
    // Move to next step
    nextStep();
  };

  const handleInterestToggle = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : prev.interests.length < 3 ? [...prev.interests, interest] : prev.interests
    }));
  };

  const nextStep = () => {
    setCurrentStep((currentStep + 1) as WizardStepType);
  };

  const prevStep = () => {
    setCurrentStep((currentStep - 1) as WizardStepType);
  };

  // Update childName and language when user data changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        childName: user.nickname?.trim() || '',
        language: user.preferredLanguage || 'en'
      }));
    }
  }, [user?.nickname, user?.preferredLanguage]);

  // Add keyboard navigation - Enter to continue
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only proceed if Enter is pressed
      if (e.key !== 'Enter') return;
      
      // Don't trigger if we're in a textarea (for the lesson input)
      const activeElement = document.activeElement;
      if (activeElement?.tagName === 'TEXTAREA') return;
      
      // Don't trigger during story generation or when story is complete
      if (currentStep >= WizardStep.Generating) return;
      
      // For step 6 (lesson step), check if we're in an input field
      if (currentStep === WizardStep.Lesson && activeElement?.tagName === 'INPUT') return;
      
      // Check if we can proceed
      if (!canProceed()) return;
      
      // Special handling for the last form step - generate story
      if (currentStep === WizardStep.Lesson) {
        generateStory();
      } else {
        nextStep();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentStep, formData]);

  // Handle deferred generation after auth loads
  useEffect(() => {
    if (pendingGeneration && !isAuthLoading) {
      // Auth has loaded, now check subscription
      if (!hasActiveSubscription) {
        setSubscriptionRequired(true);
        setIsCheckingSubscription(false);
        setPendingGeneration(false);
        // Don't change wizard step - keep user on current form step
        return;
      }
      
      // User has subscription, proceed with generation
      setPendingGeneration(false);
      setIsCheckingSubscription(false);
      setSubscriptionRequired(false);
      actuallyGenerateStory();
    }
  }, [pendingGeneration, isAuthLoading, hasActiveSubscription]);

  const generateStory = async () => {
    // Clear any previous errors
    setError(null);
    
    // If auth is still loading, defer the check
    if (isAuthLoading) {
      setIsCheckingSubscription(true);
      setPendingGeneration(true);
      return;
    }
    
    // Auth is loaded, check subscription immediately
    setIsCheckingSubscription(true);
    
    if (!hasActiveSubscription) {
      setSubscriptionRequired(true);
      setIsCheckingSubscription(false);
      // Don't change wizard step - keep user on current form step
      return;
    }
    
    // User has subscription, proceed immediately
    setIsCheckingSubscription(false);
    setSubscriptionRequired(false);
    actuallyGenerateStory();
  };

  const actuallyGenerateStory = async () => {
    // Move to generating step and start the actual generation
    setCurrentStep(WizardStep.Generating);
    setIsGenerating(true);
    setLoadingStage('thinking');
    
    // Clear previous story content
    setStoryText('');
    setStoryTitle('');
    setStoryId(null);

    // Add overall timeout protection for the entire story generation process
    const overallTimeout = setTimeout(() => {
      setError('Story generation is taking longer than expected. Please try again.');
      setIsGenerating(false);
      setCurrentStep(WizardStep.Length);
    }, 360000); // 6 minutes total timeout for illustrations

    try {
      // Choose endpoint based on whether illustrations are enabled
      const endpoint = formData.enableIllustrations ? '/api/generate-story' : '/api/generate-story-stream';
      const isStreaming = !formData.enableIllustrations;

      if (isStreaming) {
        // Use streaming approach for stories without illustrations
        await generateStoryWithStreaming();
      } else {
        // Use non-streaming approach for stories with illustrations
        await generateStoryWithIllustrations();
      }

      clearTimeout(overallTimeout);
      setCurrentStep(WizardStep.Display);
    } catch (error) {
      clearTimeout(overallTimeout);
      console.error('Story generation error:', error);
      
      // If this is a quota/credit error, the toast has already been shown
      // Just reset the UI without showing duplicate errors
      if ((error as any).isQuotaError) {
        setIsGenerating(false);
        setCurrentStep(WizardStep.Length);
        return;
      }
      
      // Check if this is just a connection loss during SSE, in which case the story might have been generated
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      
      // If the error is empty or looks like a connection issue, try to check if story was completed on server
      if (!errorMessage || errorMessage === 'An unexpected error occurred' || errorMessage.includes('connection')) {
        console.log('🔍 Checking if story was completed on server despite connection issue...');
        
        // Try to recover by checking latest story from this session
        try {
          const checkResponse = await fetch('/api/stories?limit=1', {
            credentials: 'include'
          });
          
          if (checkResponse.ok) {
            const storiesData = await checkResponse.json();
            const latestStory = storiesData.stories?.[0];
            
            // If we have a very recent story (within last 2 minutes), it's likely our story
            if (latestStory && new Date(latestStory.createdAt).getTime() > Date.now() - 120000) {
              console.log('✅ Found recently completed story, redirecting to it');
              setIsGenerating(false);
              
              // Auto-redirect to the completed story
              setTimeout(() => {
                resetGenderAfterStory();
                redirectToStory({ 
                  storyId: latestStory.id, 
                  fromWizard: true,
                  playAudio: false
                });
              }, 1000);
              return;
            }
          }
        } catch (checkError) {
          console.log('Could not check for completed story:', checkError);
        }
      }
      
      setError(errorMessage);
      setIsGenerating(false);
      setCurrentStep(WizardStep.Length);
    }
  };

  const generateStoryWithIllustrations = async () => {
    // Use SSE for stories with illustrations, fallback to regular API for non-illustration stories
    const shouldUseSSE = formData.enableIllustrations && useSSE;
    
    if (shouldUseSSE) {
      await generateStoryWithSSE();
    } else {
      await generateStoryLegacy();
    }
  };

  // New SSE-based story generation for illustrations
  const generateStoryWithSSE = async () => {
    setLoadingStage('thinking');
    setIllustrationProgress(null);
    
    try {
      const response = await fetch('/api/generate-story-with-progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({
          age: parseInt(formData.age),
          gender: formData.gender,
          language: formData.language,
          childName: formData.childName.trim(),
          interests: formData.interests,
          style: formData.style,
          lesson: formData.lesson,
          length: formData.length,
          characterProfileId: formData.characterProfileId || undefined,
          isCollaborative: isCollaborative,
          plotTwists: plotTwists.map(t => t.text),
          enableIllustrations: formData.enableIllustrations,
          characterImageUrl: formData.selectedCharacterStudioCharacter 
            ? formData.selectedCharacterStudioCharacter.cartoonImageUrl 
            : formData.selectedCartoonCharacter 
              ? formData.selectedCartoonCharacter.cartoonImageUrl 
              : formData.characterImageUrl,
          characterImageData: formData.characterImageData  // Already properly set by character selection handlers
        })
      });


      if (!response.ok) {
        throw new Error(`Failed to start story generation: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      
      if (!reader) {
        throw new Error('No response stream available');
      }

      let chunkCount = 0;
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
            break;
        }

        chunkCount++;
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete lines from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep the last incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6);
              // Handle large JSON payloads by trying to parse them safely
              const eventData = JSON.parse(jsonStr);
              await handleSSEEvent(eventData);
            } catch (parseError) {
              console.warn('❌ Failed to parse SSE data. Line length:', line.length, 'Error:', (parseError as Error)?.message || parseError);
              // Try to extract just the type if full parsing fails
              try {
                const typeMatch = line.match(/"type":"([^"]+)"/);
                if (typeMatch) {
                  console.log('📦 Extracted event type:', typeMatch[1]);
                  if (typeMatch[1] === 'complete') {
                    // For complete events, fall back to legacy generation
                    console.log('🔄 Large complete event detected, switching to legacy mode...');
                    setUseSSE(false);
                    await generateStoryLegacy();
                    return;
                  }
                }
              } catch (fallbackError) {
                console.warn('❌ Fallback parsing also failed:', fallbackError);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('SSE story generation failed:', error);
      
      // Don't fall back to legacy for quota/credit errors
      if ((error as any).isQuotaError) {
        console.log('Quota error detected, stopping generation');
        setIsGenerating(false);
        throw error;
      }
      
      // Fallback to legacy non-SSE approach for other errors
      console.log('Falling back to legacy story generation...');
      setUseSSE(false);
      try {
        await generateStoryLegacy();
      } catch (legacyError) {
        console.error('Legacy generation also failed:', legacyError);
        // Re-throw so outer catch can handle it
        throw legacyError;
      }
    }
  };

  // Handle incoming SSE events
  const handleSSEEvent = async (eventData: any) => {
    console.log('Received SSE event:', eventData);
    
    switch (eventData.type) {
      case 'progress':
        setIllustrationProgress({
          current: 0,
          total: 100,
          percentage: 0,
          stage: eventData.stage,
          message: eventData.message,
          description: eventData.message
        });
        
        // Update loading stage based on progress stage
        if (eventData.stage === 'story') {
          setLoadingStage('thinking');
        } else if (eventData.stage === 'planning') {
          setLoadingStage('writing');
        } else if (eventData.stage === 'illustrations') {
          setLoadingStage('polishing');
        }
        break;

      case 'story_complete':
        // Story text is complete, but illustrations may still be generating
        if (eventData.story) {
          setStoryText(eventData.story);
        }
        if (eventData.title) {
          setStoryTitle(eventData.title);
        }
        break;

      case 'illustration_plan':
        setIllustrationProgress({
          current: 0,
          total: eventData.totalIllustrations || 8,
          percentage: 0,
          stage: 'illustrations',
          message: eventData.message,
          description: `Preparing to create ${eventData.totalIllustrations} magical illustrations...`
        });
        break;

      case 'illustration_progress':
        setIllustrationProgress({
          current: eventData.current || 0,
          total: eventData.total || 8,
          percentage: eventData.percentage || 0,
          stage: 'illustrations',
          message: eventData.message,
          description: eventData.description
        });
        setLoadingStage('polishing');
        break;

      case 'complete':
        if (eventData.success) {
          // Set final story content
          setStoryText(eventData.story);
          setStoryTitle(eventData.title);
          setStoryId(eventData.storyId);
          setAudioText(eventData.audioText || eventData.story);

          setIllustrationProgress({
            current: 100,
            total: 100,
            percentage: 100,
            stage: 'complete',
            message: 'Your magical story is ready!',
            description: 'Story creation complete with all illustrations'
          });

          // Show nickname dialog if no previous story ID and not collaborative
          if (!storyId && !isCollaborative) {
            setShowNicknameDialog(true);
          }

          // Invalidate stories query to refresh library
          await queryClient.invalidateQueries({ queryKey: ["/api/stories"] });
          
          // Auto-redirect after completion
          setTimeout(() => {
            // Reset gender context after successful story generation
            resetGenderAfterStory();
            
            redirectToStory({ 
              storyId: eventData.storyId, 
              fromWizard: true,
              playAudio: false
            });
          }, 4000);
        }
        break;

      case 'error':
        const errorMessage = eventData.error || 'Story generation failed';
        
        // Show user-facing toast notification
        toast({
          title: "Story Generation Failed",
          description: errorMessage,
          variant: "destructive",
        });
        
        // Mark as an error to prevent fallback for credit/quota issues
        const error = new Error(errorMessage);
        (error as any).isQuotaError = errorMessage.toLowerCase().includes('credit') || 
                                      errorMessage.toLowerCase().includes('quota') ||
                                      errorMessage.toLowerCase().includes('insufficient');
        throw error;
        
      default:
        console.log('Unknown SSE event type:', eventData.type, eventData);
    }
  };

  // Legacy non-SSE story generation (fallback)
  const generateStoryLegacy = async () => {
    setLoadingStage('thinking');
    
    const controller = new AbortController();
    const requestTimeout = setTimeout(() => controller.abort(), 300000); // 5 minutes for non-streaming with illustrations
    
    const response = await fetch('/api/generate-story', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      signal: controller.signal,
      body: JSON.stringify({
        age: parseInt(formData.age),
        gender: formData.gender,
        language: formData.language,
        childName: formData.childName.trim(),
        interests: formData.interests,
        style: formData.style,
        lesson: formData.lesson,
        length: formData.length,
        isCollaborative: isCollaborative,
        plotTwists: plotTwists.map(t => t.text),
        enableIllustrations: formData.enableIllustrations,
        characterImageUrl: formData.selectedCharacterStudioCharacter 
          ? formData.selectedCharacterStudioCharacter.cartoonImageUrl 
          : formData.selectedCartoonCharacter 
            ? formData.selectedCartoonCharacter.cartoonImageUrl 
            : formData.characterImageUrl,
        characterImageData: formData.characterImageData  // Already properly set by character selection handlers
      })
    });
    
    clearTimeout(requestTimeout);

    if (!response.ok) {
      throw new Error(`Failed to generate story: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to generate story');
    }

    // Update loading stages to show progress
    setLoadingStage('writing');
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLoadingStage('polishing');
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Set the story content
    setStoryText(data.story);
    setStoryTitle(data.title);
    setStoryId(data.storyId);
    setAudioText(data.audioText || data.story);

    // Show nickname dialog if no previous story ID and not collaborative
    if (!storyId && !isCollaborative) {
      setShowNicknameDialog(true);
    }

    // Invalidate stories query to refresh library
    await queryClient.invalidateQueries({ queryKey: ["/api/stories"] });
    
    // Auto-redirect after completion
    setTimeout(() => {
      // Reset gender context after successful story generation
      resetGenderAfterStory();
      
      redirectToStory({ 
        storyId: data.storyId, 
        fromWizard: true,
        playAudio: false
      });
    }, 4000);
  };

  const generateStoryWithStreaming = async () => {
    // Build up story text as chunks arrive
    let accumulatedContent = '';
    let stageTimer: NodeJS.Timeout | null = null;
    let hasReceivedFirstChunk = false;
    let chunkTimeout: NodeJS.Timeout | null = null;
    
    try {
      // Use AbortController for timeout protection
      const controller = new AbortController();
      const requestTimeout = setTimeout(() => {
        controller.abort();
      }, 60000); // 1 minute timeout for initial response
      
      // First make the POST request to get authentication cookies and start the stream
      const response = await fetch('/api/generate-story-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          age: parseInt(formData.age),
          gender: formData.gender,
          language: formData.language,
          childName: formData.childName.trim(),
          interests: formData.interests,
          style: formData.style,
          lesson: formData.lesson,
          length: formData.length,
          characterProfileId: formData.characterProfileId || undefined,
          isCollaborative: isCollaborative,
          plotTwists: plotTwists.map(t => t.text),
          enableIllustrations: false, // Streaming doesn't support illustrations yet
          characterImageUrl: formData.selectedCharacterStudioCharacter 
            ? formData.selectedCharacterStudioCharacter.cartoonImageUrl 
            : formData.selectedCartoonCharacter 
              ? formData.selectedCartoonCharacter.cartoonImageUrl 
              : formData.characterImageUrl,
          characterImageData: formData.characterImageData  // Already properly set by character selection handlers
        })
      });
      
      clearTimeout(requestTimeout);

      if (!response.ok) {
        throw new Error(`Failed to start story generation: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Stream not available');
      }

      // Update loading stage after getting first response
      stageTimer = setTimeout(() => {
        setLoadingStage('writing');
      }, 1500);

      // Add chunk timeout protection with proper cancellation
      let streamAborted = false;
      
      const resetChunkTimeout = () => {
        if (chunkTimeout) clearTimeout(chunkTimeout);
        chunkTimeout = setTimeout(() => {
          if (!streamAborted) {
            streamAborted = true;
            // Properly cancel the stream and abort the request
            reader.cancel();
            controller.abort();
            // Set error state
            setError('Stream timed out waiting for data. Please try again.');
            setIsGenerating(false);
            setCurrentStep(5);
          }
        }, 30000); // 30 seconds between chunks
      };

      resetChunkTimeout();

      while (true && !streamAborted) {
        const { done, value } = await reader.read();
        
        if (done) {
          if (chunkTimeout) clearTimeout(chunkTimeout);
          break;
        }
        
        // Reset timeout on each successful chunk
        if (!streamAborted) {
          resetChunkTimeout();
        }
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'title') {
                setStoryTitle(data.data || 'Your Magical Bedtime Story');
                // Move to writing stage when we get the title
                if (stageTimer) clearTimeout(stageTimer);
                setLoadingStage('writing');
              } else if (data.type === 'chunk') {
                // Accumulate content and update the story text
                accumulatedContent += data.data;
                setStoryText(accumulatedContent);
                
                // Transition to story display step after first chunk
                if (!hasReceivedFirstChunk) {
                  hasReceivedFirstChunk = true;
                  setCurrentStep(WizardStep.Display);
                  setIsGenerating(true); // Keep generating flag true
                }
                
                // Update to polishing stage after substantial content
                if (accumulatedContent.length > 500 && loadingStage === 'writing') {
                  setLoadingStage('polishing');
                }
              } else if (data.type === 'complete') {
                // Final complete event with story ID
                const newStoryId = data.storyId || null;
                setStoryId(newStoryId);
                setStoryText(data.content || accumulatedContent);
                setStoryTitle(data.title || 'Your Magical Bedtime Story');
                setCurrentStep(WizardStep.Display);
                
                // Detect story moments for sound effects
                const moments = detectStoryMoments(data.content || accumulatedContent);
                setStoryMoments(moments);
                console.log('Detected story moments:', moments);
                
                // Invalidate stories query to refresh library
                await queryClient.invalidateQueries({ queryKey: ["/api/stories"] });
                
                // Redirect to story page after a short delay
                setTimeout(() => {
                  if (newStoryId) {
                    // Reset gender context after successful story generation
                    resetGenderAfterStory();
                    
                    redirectToStory({ 
                      storyId: newStoryId, 
                      fromWizard: true,
                      playAudio: false
                    });
                  }
                }, 2000);
              } else if (data.type === 'error') {
                throw new Error(data.error || 'Failed to generate story');
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }

      setCurrentStep(WizardStep.Display);
    } catch (error) {
      console.error('Error generating story:', error);
      
      // Clean up chunk timeout on error
      if (chunkTimeout) clearTimeout(chunkTimeout);
      
      // Provide user-friendly error messages based on error type
      let errorMessage = t('errorMessage');
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Story generation timed out. Our servers might be busy - please try again in a moment.';
        } else if (error.message.includes('Stream timed out')) {
          errorMessage = 'The story generation got interrupted. Please try creating your story again.';
        } else if (error.message.includes('timed out')) {
          errorMessage = 'Request timed out. Please check your connection and try again.';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      setCurrentStep(5);
    } finally {
      if (stageTimer) clearTimeout(stageTimer);
      if (chunkTimeout) clearTimeout(chunkTimeout);
      setIsGenerating(false);
      setLoadingStage('thinking'); // Reset for next time
    }
  };

  // Function to generate AI lesson suggestions
  const generateLessonSuggestions = async () => {
    if (!formData.age || !formData.gender || !formData.interests.length || !formData.style) {
      alert('Please complete the previous steps before getting AI suggestions.');
      return;
    }

    setIsLoadingSuggestions(true);
    setError(null);

    try {
      const response = await apiRequest('POST', '/api/suggest-lessons', {
        age: parseInt(formData.age),
        gender: formData.gender,
        childName: formData.childName,
        interests: formData.interests,
        style: formData.style
      }, { timeout: 60000 }); // 60 second timeout for AI suggestions

      const data = await response.json();
      console.log('Lesson suggestions response:', data);

      if (data.success && data.suggestions) {
        setLessonSuggestions(data.suggestions);
        setShowSuggestions(true);
      } else {
        console.error('Invalid response format:', data);
        setError('Failed to generate lesson suggestions. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching lesson suggestions:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
        setError('The AI is taking longer than usual to generate suggestions. Please try again.');
      } else {
        setError('Failed to generate lesson suggestions. Please try again.');
      }
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion: string) => {
    setFormData({ ...formData, lesson: suggestion });
    setShowSuggestions(false);
  };

  const resetForm = () => {
    setCurrentStep(0);
    setStoryText('');
    setStoryTitle('');
    setStoryId(null);
    setIsGenerating(false);
    setError(null);
    setIsCheckingSubscription(false);
    setSubscriptionRequired(false);
    setFormData({
      age: '',
      gender: '',
      language: 'en',
      childName: user?.nickname?.trim() || '',
      childProfileId: null,
      characterProfileId: null,
      interests: [],
      style: '',
      length: '',
      lesson: '',
      enableIllustrations: false,
      characterImageUrl: '',
      characterImageData: null,
      selectedCartoonCharacter: null,
      selectedCharacterFromGallery: null,
      selectedCharacterStudioCharacter: null
    });
    setIsCollaborative(false);
    setPlotTwists([]);
    setLessonSuggestions([]);
    setShowSuggestions(false);
    
    // Don't reset gender context here - let it persist until story generation
    
    // Clean up audio when resetting
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setIsPlaying(false);
    setCurrentWordIndex(-1);
    setStoryMoments([]);
    setLastSoundIndex(-10);
  };


  // Generate audio from the story text
  const generateAudio = async () => {
    if (!storyText || isLoadingAudio) return;

    setIsLoadingAudio(true);
    console.log('Generating audio for story:', storyText.length, 'characters');

    try {
      // Send the full story text - backend will handle chunking
      setAudioText(storyText); // Store the full text for audio

      const response = await apiRequest('POST', '/api/generate-audio', {
        text: storyText,
        storyId: storyId, // Include story ID for caching
        voice: 'nova', // Warm and friendly voice for bedtime stories
        speed: 0.9 // Slightly slower for calming effect
      }, { timeout: 180000 }); // 180 seconds timeout for longer stories with multiple chunks

      const audioBlob = await response.blob();
      console.log('Audio generated successfully:', audioBlob.size, 'bytes');
      const url = URL.createObjectURL(audioBlob);

      // Clean up previous audio URL
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      setAudioUrl(url);

      // Create new audio element
      const audio = new Audio(url);
      audioRef.current = audio;

      // Setup event listeners
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentWordIndex(-1);
      });

      audio.addEventListener('loadedmetadata', () => {
        setAudioDuration(audio.duration);
      });

      // Track playback progress for word highlighting with character-based estimation
      audio.addEventListener('timeupdate', () => {
        if (!highlightingEnabled) {
          setCurrentWordIndex(-1);
          return;
        }

        const currentAudioText = audioText || storyText; // Fallback to storyText if audioText not set
        if (audio.duration > 0 && currentAudioText) {
          const progress = audio.currentTime / audio.duration;
          const words = currentAudioText.split(/\s+/);

          // Calculate cumulative character positions with adjustment for speech patterns
          let totalChars = currentAudioText.length;
          // Apply a slight adjustment to account for natural speech pauses
          const adjustedProgress = Math.min(progress * 1.02, 1); // Slight speed-up to compensate for pauses
          let currentChars = Math.floor(adjustedProgress * totalChars);

          let charCount = 0;
          let wordIndex = -1;

          // Find the current word based on character position
          for (let i = 0; i < words.length; i++) {
            if (charCount <= currentChars && currentChars < charCount + words[i].length + 1) {
              wordIndex = i;
              break;
            }
            charCount += words[i].length + 1; // +1 for space
          }

          // Ensure we highlight the last word when near the end
          if (progress > 0.98 && wordIndex === -1) {
            wordIndex = words.length - 1;
          }

          setCurrentWordIndex(wordIndex);

          // Check for sound effects triggers
          if (soundEffectsEnabled && wordIndex >= 0) {
            const moment = shouldTriggerSound(storyMoments, wordIndex, lastSoundIndex);
            if (moment) {
              setLastSoundIndex(wordIndex);
              console.log(`Triggering ${moment.type} sound effect at word ${wordIndex}: "${moment.keyword}"`);
              // Trigger appropriate sound effect
              switch (moment.type) {
                case 'magic':
                  soundEffects.playMagicSparkle();
                  break;
                case 'adventure':
                  soundEffects.playWhoosh();
                  break;
                case 'happy':
                  soundEffects.playHappyChime();
                  break;
                case 'funny':
                  soundEffects.playBoing();
                  break;
                case 'sleepy':
                  soundEffects.playSleepyTwinkle();
                  break;
              }
            }
          }
        }
      });

      // Log audio metadata when loaded
      audio.addEventListener('loadeddata', () => {
        console.log('Audio loaded:', {
          duration: audio.duration,
          src: audio.src.substring(0, 50) + '...',
          textLength: storyText.length
        });
      });

      // Add error handler for playback
      audio.addEventListener('error', (e) => {
        console.error('Audio playback error:', e);
        const target = e.target as HTMLAudioElement;
        console.error('Audio error details:', {
          error: target.error,
          src: target.src,
          readyState: target.readyState,
          networkState: target.networkState
        });
        alert('There was an error playing the audio. Please try again.');
        setIsPlaying(false);
        setIsLoadingAudio(false);
      });

      // Don't autoplay - wait for user to click play button
      // This avoids browser autoplay restrictions
      setIsPlaying(false);
      console.log('Audio ready to play. Click the play button to start.');
    } catch (error) {
      console.error('Error generating audio:', error);
      let errorMessage = 'Sorry, I couldn\'t create the audio for this story.';

      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          errorMessage = 'The story is taking longer than expected to convert to audio. Please try again with a shorter story.';
        } else if (error.message.includes('Failed to generate audio')) {
          errorMessage = 'There was an issue with the audio service. Please try again in a moment.';
        }
      }

      alert(errorMessage);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (!audioRef.current) {
      generateAudio();
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Play with error handling
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch((error) => {
        console.error('Error playing audio:', error);
        // Don't show alert - user can just click again
        setIsPlaying(false);
      });

      // Resume word highlighting from current position with character-based estimation
      const currentAudioText = audioText || storyText; // Use the same text used for audio
      if (highlightingEnabled && audioRef.current.duration > 0 && currentAudioText) {
        const progress = audioRef.current.currentTime / audioRef.current.duration;
        const words = currentAudioText.split(/\s+/);

        // Calculate cumulative character positions with adjustment for speech patterns
        let totalChars = currentAudioText.length;
        // Apply adjustment to sync with speech timing - increased for better accuracy
        const adjustedProgress = Math.min(progress * 1.05, 1); // 5% speed-up to compensate for pauses and speech timing
        let currentChars = Math.floor(adjustedProgress * totalChars);

        let charCount = 0;
        let wordIndex = -1;

        // Find the current word based on character position
        for (let i = 0; i < words.length; i++) {
          if (charCount <= currentChars && currentChars < charCount + words[i].length + 1) {
            wordIndex = i;
            break;
          }
          charCount += words[i].length + 1; // +1 for space
        }

        // Ensure we highlight the last word when near the end
        if (progress > 0.98 && wordIndex === -1) {
          wordIndex = words.length - 1;
        }

        setCurrentWordIndex(wordIndex);
      }
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [audioUrl]);

  const canProceed = () => {
    switch (currentStep) {
      case WizardStep.Interests: return formData.interests.length > 0;
      case WizardStep.Style: return formData.style !== '';
      case WizardStep.Length: return formData.length !== '';
      case WizardStep.Lesson: return formData.lesson.trim() !== '';
      default: return true;
    }
  };

  const getStepTitle = () => {
    const titles = [
      t('interestsTitle'),
      t('styleTitle'),
      t('lengthTitle'),
      t('lessonTitle'),
      t('generatingTitle'),
      t('storyReadyTitle')
    ];
    return titles[currentStep];
  };

  const getStepSubtitle = () => {
    const subtitles = [
      t('interestsSubtitle'),
      t('styleSubtitle'),
      t('lengthSubtitle'),
      t('lessonSubtitle'),
      t('generatingSubtitle'),
      t('storyReadySubtitle')
    ];
    return subtitles[currentStep];
  };

  // Render story with highlighted words and markdown images
  const renderHighlightedStory = () => {
    // Use audioText for highlighting if it exists (audio is playing), otherwise use full story
    const content = audioText || storyText;
    
    // If highlighting is disabled or no audio is playing, render as markdown
    if (!highlightingEnabled || currentWordIndex === -1) {
      return (
        <div className="text-primary leading-relaxed text-lg prose prose-lg max-w-none">
          <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            components={{
              img: ({ node, ...props }) => (
                <img
                  {...props}
                  className="w-full max-w-md mx-auto my-6 rounded-lg shadow-lg"
                  style={{ display: 'block' }}
                  onError={(e) => {
                    console.error('Image failed to load:', props.src);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ),
              p: ({ node, ...props }) => (
                <p {...props} className="text-primary leading-relaxed text-lg mb-4" />
              )
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      );
    }
    
    // For highlighted reading, we need to handle text and images separately
    const parts = content.split(/(\!\[.*?\]\(.*?\))/);
    let wordCounter = 0;
    
    return (
      <div className="text-primary leading-relaxed text-lg prose prose-lg max-w-none">
        {parts.map((part, partIndex) => {
          // Check if this part is an image markdown
          const imageMatch = part.match(/\!\[(.*?)\]\((.*?)\)/);
          if (imageMatch) {
            return (
              <img
                key={partIndex}
                src={imageMatch[2]}
                alt={imageMatch[1]}
                className="w-full max-w-md mx-auto my-6 rounded-lg shadow-lg block"
                onError={(e) => {
                  console.error('Image failed to load:', imageMatch[2]);
                  e.currentTarget.style.display = 'none';
                }}
              />
            );
          }
          
          // Handle text content with word highlighting
          const words = part.trim().split(/\s+/).filter(word => word.length > 0);
          return (
            <p key={partIndex} className="text-primary leading-relaxed text-lg mb-4">
              {words.map((word, wordIndex) => {
                const globalWordIndex = wordCounter + wordIndex;
                const isCurrentWord = highlightingEnabled && globalWordIndex === currentWordIndex;
                
                wordCounter++;
                
                return (
                  <span
                    key={wordIndex}
                    className={`transition-all duration-300 ease-in-out ${
                      isCurrentWord
                        ? 'bg-purple-100 text-purple-900 px-0.5 rounded-sm border-b-2 border-purple-400'
                        : globalWordIndex < currentWordIndex
                        ? 'text-gray-700 opacity-90'
                        : 'text-gray-800'
                    }`}
                  >
                    {word}{' '}
                  </span>
                );
              })}
            </p>
          );
        })}
      </div>
    );
  };

  // Step -1: Child Selection
  if (currentStep === WizardStep.ChildSelect) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <div className="flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-white to-purple-50 rounded-3xl shadow-xl p-8 max-w-2xl w-full border border-purple-100">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="bg-gradient-to-r from-purple-600 to-teal-600 p-4 rounded-full">
                  <Users className="w-12 h-12 text-white" />
                </div>
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent mb-2">
                Who is this story for?
              </h1>
              <p className="text-gray-600">Select a child profile to create a personalized story</p>
            </div>

            {childProfiles.length > 0 ? (
              <div className="space-y-4 mb-6">
                {childProfiles.map((child: any) => {
                  const age = child.birthday ? calculateAge(child.birthday) : null;
                  return (
                    <button
                      key={child.id}
                      onClick={() => handleChildSelection(child)}
                      className="w-full p-6 rounded-2xl border-2 border-purple-200 bg-white hover:bg-purple-50 hover:border-purple-400 transition-all duration-200 text-left shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                      data-testid={`button-select-child-${child.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-purple-700">
                            {child.name}
                            {child.nickname && ` (${child.nickname})`}
                          </h3>
                          <div className="flex gap-4 mt-2 text-sm text-gray-600">
                            {age && <span>Age: {age}</span>}
                            {child.gender && <span>• {child.gender}</span>}
                            {child.language && <span>• {child.language.toUpperCase()}</span>}
                          </div>
                        </div>
                        <ArrowRight className="w-6 h-6 text-purple-600" />
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 mb-6 bg-purple-50 rounded-2xl border-2 border-purple-200">
                <p className="text-gray-700 mb-6 font-medium">You haven't created any child profiles yet.</p>
                <p className="text-gray-600 text-sm mb-6">Create a child profile to get started with personalized stories!</p>
              </div>
            )}

            <div className="border-t border-purple-200 pt-6">
              <Link 
                href="/child-profiles"
                className="w-full bg-gradient-to-r from-purple-600 to-teal-600 text-white py-3 px-6 rounded-2xl font-bold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                data-testid="button-create-child-profile"
              >
                Create a Child Profile
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Step 0: Interests
  if (currentStep === WizardStep.Interests) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <div className="flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-white to-purple-50 rounded-3xl shadow-xl p-8 max-w-lg w-full border border-purple-100">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img 
                  src={getGenderBasedLogo()} 
                  alt="SnuggleCrafters Logo" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent mb-2">{getStepTitle()}</h1>
              <p className="text-gray-600">{getStepSubtitle()}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {interests.map((interest) => (
              <button
                key={interest}
                onClick={() => handleInterestToggle(interest)}
                className={`py-3 px-4 rounded-xl border-2 transition-all duration-200 text-center font-medium text-sm transform hover:scale-[1.02] ${
                  formData.interests.includes(interest)
                    ? 'bg-gradient-to-r from-purple-600 to-teal-600 text-purple-50 border-transparent shadow-lg hover:shadow-xl'
                    : 'bg-white text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300 shadow-md hover:shadow-lg'
                }`}
              >
                {interest}
              </button>
              ))}
            </div>

            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className="w-full bg-gradient-to-r from-purple-600 to-teal-600 text-white py-3 px-6 rounded-2xl font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02] mt-6"
            >
              {t('continueButton')}
              <ArrowRight className="w-4 h-4" />
            </button>

            <StepIndicator step={1} />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Step 1: Style
  if (currentStep === WizardStep.Style) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <div className="flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-white to-purple-50 rounded-3xl shadow-xl p-8 max-w-md w-full border border-purple-100">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img 
                  src={getGenderBasedLogo()} 
                  alt="SnuggleCrafters Logo" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent mb-2">{getStepTitle()}</h1>
              <p className="text-gray-600">{getStepSubtitle()}</p>
            </div>

            <div className="space-y-3 mb-8">
              {styles.map((style) => (
                <button
                key={style.value}
                onClick={() => setFormData({...formData, style: style.value})}
                className={`w-full py-4 px-6 rounded-2xl border-2 transition-all duration-200 text-left font-medium flex items-center gap-4 transform hover:scale-[1.02] focus:outline-none focus:ring-4 ${
                  formData.style === style.value
                    ? 'bg-gradient-to-r from-purple-700 to-teal-700 text-white border-transparent shadow-lg hover:shadow-xl hover:from-purple-800 hover:to-teal-800 focus:ring-purple-300'
                    : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50 hover:border-purple-400 shadow-md hover:shadow-lg focus:ring-purple-200'
                }`}
              >
                <span className="text-3xl">{style.emoji}</span>
                <div>
                  <div className="font-bold">{style.label}</div>
                  <div className="text-sm opacity-70">{style.desc}</div>
                </div>
                </button>
              ))}
            </div>

            <div className="flex gap-4">
              <button
                onClick={prevStep}
                className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-900 py-3 px-6 rounded-2xl font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('backButton')}
              </button>
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className="flex-2 bg-gradient-to-r from-purple-600 to-teal-600 text-white py-3 px-6 rounded-2xl font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
              >
                {t('continueButton')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <StepIndicator step={2} />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Step 2: Length selection
  if (currentStep === WizardStep.Length) {
    const lengthOptions = [
      { value: 'short', label: t('shortLabel'), emoji: '⏱️', desc: t('shortDesc') },
      { value: 'long', label: t('longLabel'), emoji: '📖', desc: t('longDesc') }
    ];

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <div className="flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-white to-purple-50 rounded-3xl shadow-xl p-8 max-w-md w-full border border-purple-100">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img 
                  src={getGenderBasedLogo()} 
                  alt="SnuggleCrafters Logo" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent mb-2">{getStepTitle()}</h1>
              <p className="text-gray-600">{getStepSubtitle()}</p>
            </div>

            <div className="space-y-3 mb-8">
              {lengthOptions.map((length) => (
                <button
                  key={length.value}
                  onClick={() => setFormData({...formData, length: length.value})}
                  className={`w-full py-4 px-6 rounded-2xl border-2 transition-all duration-200 text-left font-medium flex items-center gap-4 transform hover:scale-[1.02] focus:outline-none focus:ring-4 ${
                    formData.length === length.value
                      ? 'bg-gradient-to-r from-purple-700 to-teal-700 text-white border-transparent shadow-lg hover:shadow-xl hover:from-purple-800 hover:to-teal-800 focus:ring-purple-300'
                      : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50 hover:border-purple-400 shadow-md hover:shadow-lg focus:ring-purple-200'
                  }`}
                  data-testid={`button-length-${length.value}`}
                >
                  <span className="text-3xl">{length.emoji}</span>
                  <div>
                    <div className="font-bold">{length.label}</div>
                    <div className="text-sm opacity-70">{length.desc}</div>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex gap-4">
              <button
                onClick={prevStep}
                className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-900 py-3 px-6 rounded-2xl font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                data-testid="button-back"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('backButton')}
              </button>
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className="flex-1 bg-gradient-to-r from-purple-600 to-teal-600 text-white py-3 px-6 rounded-2xl font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02]"
                data-testid="button-continue"
              >
                {t('continueButton')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <StepIndicator step={3} />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Step 3: Lesson
  if (currentStep === WizardStep.Lesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <div className="flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-white to-purple-50 rounded-3xl shadow-xl p-8 max-w-md w-full border border-purple-100">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img 
                  src={getGenderBasedLogo()} 
                  alt="SnuggleCrafters Logo" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent mb-2">{getStepTitle()}</h1>
              <p className="text-gray-600">{getStepSubtitle()}</p>
            </div>

          <div className="space-y-6">
            <div className="space-y-4">
              <textarea
                value={formData.lesson}
                onChange={(e) => setFormData({...formData, lesson: e.target.value})}
                placeholder={t('lessonPlaceholder')}
                className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:border-purple-600 focus:outline-none transition-colors resize-none h-32 text-lg"
                autoFocus
              />

              {/* AI Suggestions Button */}
              <div className="flex justify-center">
                <button
                  onClick={generateLessonSuggestions}
                  disabled={isLoadingSuggestions || !formData.age || !formData.gender || !formData.interests.length || !formData.style}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium transition-all duration-200 transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  data-testid="button-ai-suggestions"
                >
                  {isLoadingSuggestions ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {isLoadingSuggestions ? 'Getting Ideas...' : 'Get AI Suggestions'}
                </button>
              </div>

              {/* AI Suggestions Display */}
              {showSuggestions && lessonSuggestions.length > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-amber-800">AI Lesson Suggestions</h3>
                  </div>
                  <div className="space-y-2">
                    {lessonSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => selectSuggestion(suggestion)}
                        className="w-full text-left p-3 bg-white/80 hover:bg-white rounded-xl border border-amber-200 hover:border-amber-300 transition-all duration-200 text-gray-700 hover:text-gray-900 hover:shadow-md transform hover:scale-[1.02]"
                        data-testid={`suggestion-option-${index}`}
                      >
                        "{suggestion}"
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={() => setShowSuggestions(false)}
                      className="text-sm text-amber-700 hover:text-amber-800 underline"
                      data-testid="button-close-suggestions"
                    >
                      Close suggestions
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Story Options */}
            <div className="mt-6">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-800">Story Options</h3>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Label htmlFor="enable-illustrations" className="text-sm font-medium text-gray-700">
                      Generate illustrations
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">
                      Add beautiful AI-generated images to bring your story to life
                    </p>
                  </div>
                  <Switch
                    id="enable-illustrations"
                    checked={formData.enableIllustrations}
                    onCheckedChange={(checked) => 
                      setFormData({...formData, enableIllustrations: checked})
                    }
                    data-testid="switch-enable-illustrations"
                    className="data-[state=checked]:bg-purple-600 data-[state=unchecked]:bg-[#fef3c7]"
                  />
                </div>
              </div>
            </div>

            {/* Character Selection and Illustration Generation */}
            {formData.enableIllustrations && (
              <div className="mt-6">
                <IllustrationsTogglePanel
                  storyId="temp-story-id" // This will be replaced with actual story ID after generation
                  childAge={parseInt(formData.age)}
                  scenes={[]} // This will be populated with actual scenes after story generation
                  selectedCharacter={formData.selectedCharacterStudioCharacter}
                  onCharacterSelected={(character) => {
                    setFormData({
                      ...formData,
                      selectedCharacterStudioCharacter: character,
                      // Clear other character selections when selecting from character studio
                      selectedCartoonCharacter: null,
                      selectedCharacterFromGallery: null,
                      characterProfileId: null,
                      characterImageUrl: character ? character.cartoonImageUrl : '',
                      characterImageData: character ? {
                        originalUrl: character.originalPhotoUrl,
                        processedUrl: character.cartoonImageUrl,
                        characterFeatures: character.characterTraits || {}
                      } : null
                    });
                  }}
                  onGenerated={(payload) => {
                    console.log("Illustrations generated:", payload);
                    // Handle the generated illustrations
                    // This will be integrated with the story generation flow
                  }}
                />
              </div>
            )}


            {/* Collaborative Story Mode */}
            <div className="mt-6">
              <CollaborativeStoryMode
                isEnabled={isCollaborative}
                plotTwists={plotTwists}
                onAddPlotTwist={(twist) => {
                  setPlotTwists([...plotTwists, { id: Date.now().toString(), text: twist }]);
                }}
                onRemovePlotTwist={(id) => {
                  setPlotTwists(plotTwists.filter(t => t.id !== id));
                }}
                onToggleMode={() => setIsCollaborative(!isCollaborative)}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={prevStep}
                className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-900 py-3 px-6 rounded-2xl font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('backButton')}
              </button>
              <button
                onClick={generateStory}
                disabled={!canProceed()}
                className="flex-2 bg-gradient-to-r from-purple-600 to-teal-600 text-white py-3 px-6 rounded-2xl font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
              >
                {t('createStoryButton')}
                <Book className="w-4 h-4" />
              </button>
            </div>
          </div>

            <StepIndicator step={4} />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Step 4: Loading
  if (currentStep === WizardStep.Generating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 flex items-center justify-center p-4">
        <PenelopeWelcome isFounder={isFounder} />
        <PenelopeEasterEggs isFounder={isFounder} currentStep={currentStep} isGenerating={true} />
        <div className="bg-gradient-to-br from-white to-purple-50 rounded-3xl shadow-xl max-w-2xl w-full border border-purple-100">
          {subscriptionRequired ? (
            <NonSubscriberLoadingAnimation 
              message="Subscription required for story generation"
              showUpgradePrompt={true}
            />
          ) : isCheckingSubscription ? (
            <NonSubscriberLoadingAnimation 
              message="Checking subscription status..."
              showUpgradePrompt={false}
            />
          ) : (
            <StoryLoadingAnimation 
              stage={loadingStage} 
              illustrationProgress={illustrationProgress}
            />
          )}
        </div>
      </div>
    );
  }

  // Step 7: Story Display
  if (currentStep === WizardStep.Display) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <div className="p-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-white to-purple-50 rounded-3xl shadow-xl overflow-hidden border border-purple-100">
              <div className="bg-gradient-to-r from-purple-600 to-teal-600 p-6">
                <div className="text-center">
                  <h1 className="text-3xl font-bold text-white mb-2 flex items-center justify-center gap-3">
                    <Book className="w-8 h-8" />
                    {getStepTitle()}
                  </h1>
                  <p className="text-purple-100">{getStepSubtitle()}</p>
                </div>
              </div>

              <div className="p-8">
                {/* Audio Player Controls */}
                <div className="mb-6 space-y-4">
                  <div className="flex justify-center">
                    <button
                      onClick={togglePlayPause}
                      disabled={isLoadingAudio}
                      className={`flex items-center gap-3 px-6 py-3 rounded-full font-medium transition-all duration-200 transform hover:scale-105 ${
                        isLoadingAudio 
                          ? 'bg-gray-200 text-gray-700 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-purple-600 to-teal-600 text-purple-50 hover:from-purple-700 hover:to-teal-700 shadow-lg focus-visible:ring-purple-600'
                      }`}
                    >
                      {isLoadingAudio ? (
                        <AudioLoadingAnimation />
                      ) : isPlaying ? (
                        <>
                          <Pause className="w-5 h-5" />
                          <span>Pause Story</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-5 h-5" />
                          <span>Listen to Story</span>
                        </>
                      )}
                      <Volume2 className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Highlighting Toggle */}
                  {(audioUrl || isPlaying) && (
                    <div className="flex justify-center">
                      <label className="flex items-center gap-2 cursor-pointer px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
                        <input
                          type="checkbox"
                          checked={highlightingEnabled}
                          onChange={(e) => {
                            setHighlightingEnabled(e.target.checked);
                            if (!e.target.checked) {
                              setCurrentWordIndex(-1);
                            }
                          }}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">Highlight words as they're read</span>
                      </label>
                    </div>
                  )}

                  {/* Sound Effects Toggle */}
                  {(audioUrl || isPlaying) && (
                    <div className="flex justify-center">
                      <label className="flex items-center gap-2 cursor-pointer px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
                        <input
                          type="checkbox"
                          checked={soundEffectsEnabled}
                          onChange={(e) => {
                            setSoundEffectsEnabled(e.target.checked);
                          }}
                          className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                        />
                        <Music className="w-4 h-4 text-teal-600" />
                        <span className="text-sm text-gray-700">Play whimsical sound effects</span>
                      </label>
                    </div>
                  )}
                </div>

                <div className="prose prose-lg max-w-none mb-8">
                  {/* Story Title */}
                  {storyTitle && (
                    <h2 className="text-3xl font-bold text-primary text-center mb-6 flex items-center justify-center gap-3">
                      <Book className="w-8 h-8 text-secondary" />
                      {storyTitle}
                      <Book className="w-8 h-8 text-secondary" />
                    </h2>
                  )}

                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border-l-4 border-accent">
                    {highlightingEnabled && (isPlaying || currentWordIndex >= 0) ? (
                      renderHighlightedStory()
                    ) : storyText ? (
                      <div className="prose prose-lg max-w-none text-primary leading-relaxed text-lg">
                        <ReactMarkdown
                          rehypePlugins={[rehypeRaw]}
                        >
                          {storyText}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-primary text-center italic">No story content available</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-center gap-2 text-primary mb-4">
                    <Moon className="w-5 h-5" />
                    <span className="text-sm font-medium">{t('sweetDreams')}</span>
                    <Moon className="w-5 h-5" />
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={resetForm}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-teal-600 text-white py-4 px-6 rounded-2xl font-bold text-lg transition-all duration-200 transform hover:scale-[1.02] hover:shadow-xl flex items-center justify-center gap-3 shadow-lg"
                    >
                      <Book className="w-6 h-6" />
                      {t('createAnotherStory')}
                    </button>

                    <Link href="/stories">
                      <button className="flex-1 bg-gradient-to-r from-teal-600 to-amber-600 text-white py-4 px-6 rounded-2xl font-bold text-lg transition-all duration-200 transform hover:scale-[1.02] hover:shadow-xl flex items-center justify-center gap-3 shadow-lg">
                        <Library className="w-6 h-6" />
                        View All Stories
                      </button>
                    </Link>
                  </div>
                </div>

                {/* Share Story Button */}
                {storyId && (
                  <div className="mt-8 flex justify-center">
                    <ShareStory story={{ id: storyId, title: storyTitle, content: storyText } as any} />
                  </div>
                )}

                {/* Story Enhancements Section */}
                {storyId && (
                  <div className="mt-12">
                    <StoryEnhancements
                      storyId={storyId}
                      storyTitle={storyTitle}
                      storyContent={storyText}
                    />
                  </div>
                )}

                {/* Sticker Generator Section */}
                {storyId && (
                  <div className="mt-8">
                    <StickerGenerator
                      storyId={storyId}
                      storyTitle={storyTitle}
                    />
                  </div>
                )}

                {/* Merchandise Section */}
                {storyId && (
                  <div className="mt-8">
                    <MerchandiseCatalog 
                      storyId={storyId} 
                      storyTitle={storyTitle}
                    />
                  </div>
                )}
              </div>
              {/* Duplicate merchandise section removed */}
            </div>
          </div>
        </div>
        <Footer />
        
      </div>
    );
  }

  return null;
};

export default BedtimeStoryGeneratorInner;